/**
 * Code-based TanStack Router tree.
 *
 * No file-based routing / router plugin — the whole tree is assembled here from
 * the route components under `src/routes`. The router carries an `auth` context
 * (see `RouterContext`) so `beforeLoad` guards can check the signed-in user
 * without importing React hooks.
 *
 * Tree:
 *   __root__
 *   ├── /login              (public)
 *   └── _authenticated      (layout, guarded — redirects to /login when signed out)
 *       ├── /                       → chat (new conversation)
 *       ├── /conversations/$id      → chat (active conversation)
 *       ├── /reminders
 *       ├── /clients
 *       ├── /memories
 *       └── /settings
 */
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router'

import { AppShell } from '@/components/app-shell'
import type { AuthContextValue } from '@/lib/auth'
import { AdminRoute } from '@/routes/admin'
import { ChatRoute } from '@/routes/chat'
import { ClientsRoute } from '@/routes/clients'
import { ConversationRoute } from '@/routes/conversation'
import { LoginRoute } from '@/routes/login'
import { MemoriesRoute } from '@/routes/memories'
import { RemindersRoute } from '@/routes/reminders'
import { SettingsRoute } from '@/routes/settings'

/** Router context — the live auth state, injected from React (see main.tsx). */
export interface RouterContext {
  auth: AuthContextValue
}

/** Search params accepted by the login route (used to preserve the intended destination). */
export interface LoginSearch {
  redirect?: string
}

/** Search params accepted by the chat route ('/') — encaminhar um lembrete ao chat. */
export interface ChatSearch {
  reminderId?: string
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
})

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
  }),
  beforeLoad: ({ context, search }) => {
    // Already signed in → skip the login screen, honour the intended destination.
    if (context.auth.user) {
      throw redirect({ to: search.redirect ?? '/' })
    }
  },
  component: LoginRoute,
})

/**
 * Authenticated layout route. Guards all children: an unauthenticated visitor is
 * redirected to /login with the attempted path preserved in `?redirect=`.
 */
const authenticatedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_authenticated',
  beforeLoad: ({ context, location }) => {
    if (!context.auth.user) {
      throw redirect({
        to: '/login',
        search: { redirect: location.href },
      })
    }
  },
  component: AppShell,
})

const chatRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/',
  validateSearch: (search: Record<string, unknown>): ChatSearch => ({
    reminderId: typeof search.reminderId === 'string' ? search.reminderId : undefined,
  }),
  component: ChatRoute,
})

const conversationRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: 'conversations/$id',
  component: ConversationRoute,
})

const remindersRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/reminders',
  component: RemindersRoute,
})

const clientsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/clients',
  component: ClientsRoute,
})

const memoriesRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/memories',
  component: MemoriesRoute,
})

const settingsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/settings',
  component: SettingsRoute,
})

/**
 * Painel administrativo. Além do guard de autenticação herdado do layout, exige
 * o claim `admin`; um usuário sem ele é redirecionado ao início.
 */
const adminRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/admin',
  beforeLoad: ({ context }) => {
    if (context.auth.claims?.admin !== true) throw redirect({ to: '/' })
  },
  component: AdminRoute,
})

const routeTree = rootRoute.addChildren([
  loginRoute,
  authenticatedRoute.addChildren([
    chatRoute,
    conversationRoute,
    remindersRoute,
    clientsRoute,
    memoriesRoute,
    settingsRoute,
    adminRoute,
  ]),
])

/**
 * Build the router with the given auth context. Called once from `main.tsx`; the
 * auth state is kept fresh via `router.update({ context })` when auth changes.
 */
export function createAppRouter(auth: AuthContextValue) {
  return createRouter({
    routeTree,
    context: { auth },
    defaultPreload: 'intent',
    // Auth lives in React state and is re-injected on change, so never treat
    // context as stale between renders.
    defaultPreloadStaleTime: 0,
    scrollRestoration: true,
  })
}

export type AppRouter = ReturnType<typeof createAppRouter>

// Register the router instance type for end-to-end type-safety of Link/navigate.
declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter
  }
}
